import { inject, Injectable } from '@angular/core';
import { CompanyController } from '@domain';
import { PdfPreviewBuilderService } from 'src/app/modules/document/pages/pdf-preview/pdf-preview-builder.service';
import {
    normalizeSourceCompany,
    normalizeSourceDocument,
} from 'src/app/modules/document/pdf-renderer/normalize';

/** O que a prévia precisa de um modelo (interno ou da empresa). */
export interface TemplatePreviewInput {
    /** Identifica o modelo no cache (ex.: `internal-<id>`). */
    key: string;
    title: string;
    type: string;
    sessions: unknown[];
    /** Só modelos da empresa têm: config, cabeçalho e rodapé próprios. */
    documentConfig?: Record<string, unknown>;
    header?: unknown[];
    footer?: unknown[];
}

/**
 * Ajustes da folha da MINIATURA, por cima do srcdoc da prévia do PDF: sem o palco cinza,
 * sem sombra e sem margem entre folhas (quem desenha o papel é o card), e sem barra de
 * rolagem. O resto é a prévia de verdade.
 */
const THUMBNAIL_CSS = `<style>
html, body { background: transparent !important; overflow: hidden !important; }
.pagedjs_pages { padding: 0 !important; }
.pagedjs_page { box-shadow: none !important; margin: 0 !important; }
.preview-status, .preview-toolbar { display: none !important; }
</style>`;

/**
 * Gera a primeira folha de um modelo com o MESMO renderizador da prévia do PDF (o do
 * editor e do app), para a galeria do wizard mostrar o modelo como ele vai sair, no estilo
 * da miniatura do Word.
 *
 * O documento é montado em memória a partir do modelo: cabeçalho, rodapé e config vêm da
 * empresa atual (é o que o documento criado herda), sem capa e sem sumário (a primeira
 * folha seria só a capa) e sem quebra de página por seção (a folha mostraria só a primeira
 * seção).
 *
 * As prévias são geradas uma de cada vez e guardadas por modelo: abrir o wizard de novo,
 * ou voltar ao passo, não refaz o trabalho.
 */
@Injectable({ providedIn: 'root' })
export class TemplatePreviewService {
    private _builder = inject(PdfPreviewBuilderService);
    private _companies = inject(CompanyController);

    private _cache = new Map<string, Promise<string | null>>();
    private _queue: Promise<unknown> = Promise.resolve();

    public srcdocFor(input: TemplatePreviewInput): Promise<string | null> {
        const cached = this._cache.get(input.key);
        if (cached) return cached;

        const job = this._queue.then(() => this._build(input)).catch(() => null);
        // A fila segue mesmo se uma prévia falhar.
        this._queue = job;
        this._cache.set(input.key, job);
        return job;
    }

    private async _build(input: TemplatePreviewInput): Promise<string | null> {
        const plain = (value: unknown): any =>
            value === null || value === undefined ? undefined : JSON.parse(JSON.stringify(value));

        const rawCompany = plain(this._companies.currentCompany());
        const companyConfig = rawCompany?.documentConfig ?? {};

        const rawDocument = {
            _id: `template-preview-${input.key}`,
            title: input.title,
            type: input.type,
            companyId: rawCompany?._id ?? '',
            orderId: '',
            sessions: plain(input.sessions) ?? [],
            header: plain(input.header) ?? companyConfig.defaultHeader ?? [],
            footer: plain(input.footer) ?? companyConfig.defaultFooter ?? [],
            config: {
                ...(companyConfig.configs ?? {}),
                ...(plain(input.documentConfig) ?? {}),
                showCover: false,
                showSummary: false,
                sectionPageBreak: false,
            },
        };

        const srcdoc = await this._builder.build({
            document: normalizeSourceDocument(rawDocument),
            company: rawCompany ? normalizeSourceCompany(rawCompany) : undefined,
        });

        return srcdoc.replace('</head>', () => `${THUMBNAIL_CSS}</head>`);
    }
}
